import { ReactElement } from 'react';

import { MedicalInformationType } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { assertUnreachable, createInterpolatedTranslationKey, Translate, useTranslate } from '@dreryk/react-common';
import { Badge, BadgeType, StyledFlex } from '@dreryk/react-components';

import { medicalDocumentationCreateTranslationKeys, patientMedicalInformationTranslationKeys } from '../translations';

import { ChronicDiseaseInformationTable } from './chronicDisease/ChronicDiseaseInformationTable';
import { ChronicMedicalInformationAccordionSectionLabel } from './chronicDisease/ChronicMedicalInformationAccordionSectionLabel';
import { DiagnosisInformationAccordionSectionDecorator } from './diagnosis/DiagnosesInformationAccordionSectionDecorator';
import { DiagnosisInformationTable } from './diagnosis/DiagnosisInformationTable';
import { GynecologicalInterviewTable } from './gynecologicalInterview/GynecologicalInterviewTable';
import { MedicalImagingTable } from './medicalImaging/MedicalImagingTable';
import { MedicalInformationTable } from './table/MedicalInformationTable';

type MedicalInformationAccordionSectionContentProps = {
   readonly medicalInformationType: MedicalInformationType;
   readonly patientId: number;
   readonly highlightedEntryId?: number;
};

export const MedicalInformationAccordionSectionContent = (
   props: MedicalInformationAccordionSectionContentProps
): JSX.Element => {
   switch (props.medicalInformationType) {
      case 'CHRONIC_DISEASE':
         return (
            <ChronicDiseaseInformationTable patientId={props.patientId} highlightedEntryId={props.highlightedEntryId} />
         );
      case 'GYNECOLOGICAL_INTERVIEW':
         return (
            <GynecologicalInterviewTable patientId={props.patientId} highlightedEntryId={props.highlightedEntryId} />
         );
      case 'DIAGNOSIS':
         return <DiagnosisInformationTable patientId={props.patientId} highlightedEntryId={props.highlightedEntryId} />;
      case 'MEDICAL_IMAGING':
         return <MedicalImagingTable patientId={props.patientId} highlightedEntryId={props.highlightedEntryId} />;
      case 'ALLERGY':
      case 'ANTHROPOMETRY':
      case 'RISK_FACTOR':
      case 'HOSPITALIZATION':
      case 'MEDICAL_CONSULTATION':
      case 'REHABILITATION':
      case 'REFERRAL':
      case 'ANALYTICAL_TEST_RESULT':
      case 'TREATMENT':
      case 'OTHER':
         return (
            <MedicalInformationTable
               medicalInformationType={props.medicalInformationType}
               patientId={props.patientId}
               highlightedEntryId={props.highlightedEntryId}
            />
         );
      default:
         return assertUnreachable(props.medicalInformationType);
   }
};

type DefaultMedicalInformationAccordionSectionLabelProps = {
   readonly medicalInformationType: MedicalInformationType;
   readonly medicalInformationCount: number;
};

const DefaultMedicalInformationAccordionSectionLabel = (
   props: DefaultMedicalInformationAccordionSectionLabelProps
): JSX.Element => {
   const translate = useTranslate();
   return (
      <Translate>
         {createInterpolatedTranslationKey(patientMedicalInformationTranslationKeys.sectionName, {
            name: translate(medicalDocumentationCreateTranslationKeys.categories[props.medicalInformationType]),
            count: props.medicalInformationCount,
         })}
      </Translate>
   );
};

type MedicalInformationAccordionSectionLabelProps = {
   readonly medicalInformationType: MedicalInformationType;
   readonly medicalInformationCount: number;
   readonly patientId: number;
};

export const MedicalInformationAccordionSectionLabel = (
   props: MedicalInformationAccordionSectionLabelProps
): JSX.Element => {
   switch (props.medicalInformationType) {
      case 'CHRONIC_DISEASE':
         return <ChronicMedicalInformationAccordionSectionLabel patientId={props.patientId} />;
      case 'GYNECOLOGICAL_INTERVIEW':
      case 'ALLERGY':
      case 'ANTHROPOMETRY':
      case 'RISK_FACTOR':
      case 'HOSPITALIZATION':
      case 'MEDICAL_CONSULTATION':
      case 'REHABILITATION':
      case 'DIAGNOSIS':
      case 'REFERRAL':
      case 'ANALYTICAL_TEST_RESULT':
      case 'MEDICAL_IMAGING':
      case 'TREATMENT':
      case 'OTHER':
         return (
            <DefaultMedicalInformationAccordionSectionLabel
               medicalInformationCount={props.medicalInformationCount}
               medicalInformationType={props.medicalInformationType}
            />
         );
      default:
         return assertUnreachable(props.medicalInformationType);
   }
};

type MedicalInformationAccordionSectionDecoratorProps = {
   readonly medicalInformationType: MedicalInformationType;
   readonly open: boolean;
   readonly unsignedAttachmentCount: number;
};

export const MedicalInformationAccordionSectionDecorator = (
   props: MedicalInformationAccordionSectionDecoratorProps
): ReactElement | null => {
   switch (props.medicalInformationType) {
      case 'DIAGNOSIS':
         return <DiagnosisInformationAccordionSectionDecorator />;
      case 'CHRONIC_DISEASE':
      case 'GYNECOLOGICAL_INTERVIEW':
      case 'ALLERGY':
      case 'ANTHROPOMETRY':
      case 'RISK_FACTOR':
      case 'HOSPITALIZATION':
      case 'MEDICAL_CONSULTATION':
      case 'REHABILITATION':
      case 'REFERRAL':
      case 'ANALYTICAL_TEST_RESULT':
      case 'MEDICAL_IMAGING':
      case 'TREATMENT':
      case 'OTHER':
         return (
            <Badge
               testId={'unsignedAttachmentCount'}
               count={props.unsignedAttachmentCount}
               type={BadgeType.NEGATIVE}
               visible={Boolean(!props.open && props.unsignedAttachmentCount > 0)}
            />
         );
      default:
         return assertUnreachable(props.medicalInformationType);
   }
};

type MedicalInformationAccordionSectionHeaderProps = {
   readonly label: ReactElement;
   readonly decorator: ReactElement | null;
};

export const MedicalInformationAccordionSectionHeader = (
   props: MedicalInformationAccordionSectionHeaderProps
): JSX.Element => {
   return (
      <StyledFlex direction={'row'} gap={'4px'} alignItems={'center'}>
         {props.label}
         {props.decorator}
      </StyledFlex>
   );
};
